import { apiUrl } from "../../../api";
import { SimpleHybridDistanceDetector } from "./distance-detector";

export class WebAudioRecognition {
    private audioContext: AudioContext | null = null;
    private mediaStream: MediaStream | null = null;
    private websocket: WebSocket | null = null;
    private isRecording = false;
    private clientId: string | null = null;
    private onResult: (text: string, isFinal: boolean) => void;
    private onError: (error: string) => void;
    private onCancel?: () => void;
    private audioWorklet: AudioWorkletNode | null = null;

    // 音频处理参数
    private silenceThreshold = 0.005; // 降低静音阈值
    private speechThreshold = 0.02; // 添加语音阈值
    private silenceDuration = 60000; // 静音持续时间改为60秒（毫秒）
    private isSpeakingFalseDelay = 2500; // 发送is_speaking: false的延迟时间（毫秒）
    private lastSpeechTime = 0; // 上次检测到语音的时间
    private isSilent = true; // 当前是否静音，默认为静音
    private recordingStartTime = 0; // 录音开始时间
    private bufferSize = 4096; // 缓冲区大小
    private sampleRate = 16000; // 采样率
    private hasSentIsSpeakingFalse = true; // 是否已经发送过is_speaking: false，默认为true避免开始就发送
    
    // 新增：距离检测器
    // private distanceDetector: SimpleHybridDistanceDetector;
    // private enableDistanceCheck = true; // 是否启用距离检测
    
    // // 距离检测配置
    // public setDistanceCheckEnabled(enabled: boolean): void {
    //     this.enableDistanceCheck = enabled;
    //     if (!enabled) {
    //         console.log('🔧 距离检测已禁用');
    //         this.distanceDetector.reset();
    //     } else {
    //         console.log('🔧 距离检测已启用（将在首次检测到语音时自动计算基准值）');
    //     }
    // }
    
    // public getDistanceCheckStatus(): boolean {
    //     return this.enableDistanceCheck;
    // }
    
    // // 获取距离检测详细状态
    // public getDistanceCheckDetails(): { enabled: boolean; baseline: number; threshold: number } {
    //     const status = this.distanceDetector.getStatus();
    //     return {
    //         enabled: this.enableDistanceCheck,
    //         baseline: status.baseline,
    //         threshold: status.threshold
    //     };
    // }

    constructor(
        onResult: (text: string, isFinal: boolean) => void,
        onError: (error: string) => void,
        onCancel?: () => void,
    ) {
        this.onResult = onResult;
        this.onError = onError;
        this.onCancel = onCancel;
        // 初始化距离检测器
        // this.distanceDetector = new SimpleHybridDistanceDetector();
    }

    async startRecording(): Promise<void> {
        try {
            console.log('🎤 开始 WebAudio 录音...');
            console.log('📱 用户代理:', navigator.userAgent);
            console.log('🔒 是否HTTPS:', window.location.protocol === 'https:');

            // 检查HTTPS
            if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
                throw new Error('非HTTPS环境，手机浏览器可能无法访问麦克风');
            }

            // 连接 WebSocket
            console.log('🔗 连接WebSocket...');
            let url = `${apiUrl}/ws/audio/binary`;
            
            // 检查URL是否包含WebSocket协议，如果没有则添加
            if (!url.startsWith('ws://') && !url.startsWith('wss://')) {
                // 根据当前页面协议决定使用ws还是wss
                const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                url = `${protocol}//${url.replace(/^https?:\/\//, '')}`;
            }
            
            this.websocket = new WebSocket(url);

            this.websocket.onopen = async () => {
                console.log('🔗 WebSocket 连接已建立');

                try {
                    // 针对手机浏览器的音频配置
                    const audioConstraints = {
                        audio: {
                            echoCancellation: true,
                            noiseSuppression: true,
                            autoGainControl: true,
                            sampleRate: this.sampleRate,
                            channelCount: 1,
                            // 手机浏览器特殊配置
                            ...(navigator.userAgent.includes('Mobile') && {
                                sampleRate: { ideal: this.sampleRate, min: 8000, max: 48000 },
                                channelCount: { ideal: 1, min: 1, max: 2 }
                            })
                        }
                    };

                    console.log('📋 音频配置:', audioConstraints);

                    // 获取音频流
                    this.mediaStream = await navigator.mediaDevices.getUserMedia(audioConstraints);
                    console.log('✅ 音频流获取成功:', this.mediaStream);

                    // 创建 AudioContext（针对手机浏览器的特殊处理）
                    try {
                        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
                            sampleRate: this.sampleRate,
                            latencyHint: 'interactive'
                        });
                        console.log('✅ AudioContext创建成功');
                    } catch (audioError) {
                        console.warn('⚠️ AudioContext创建失败，尝试备用方案:', audioError);
                        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                    }

                    // 检查AudioContext状态
                    if (this.audioContext.state === 'suspended') {
                        console.log('⏸️ AudioContext被暂停，尝试恢复...');
                        await this.audioContext.resume();
                    }

                    // 创建音频源
                    const source = this.audioContext.createMediaStreamSource(this.mediaStream);

                    // 创建音频处理节点
                    await this.setupAudioProcessing(source);

                    this.isRecording = true;
                    this.recordingStartTime = Date.now();
                    this.lastSpeechTime = this.recordingStartTime; // 初始化语音时间
                    console.log('🎵 WebAudio 录音已开始');

                } catch (error) {
                    console.error('❌ 音频流获取失败:', error);
                    this.onError('音频流获取失败: ' + error);
                }
            };

            this.websocket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    console.log('📨 WebSocket 收到消息:', data);

                    if (data.type === "recognition_result") {
                        this.onResult(data.content, data.isFinal);
                    } else if (data.type === "error") {
                        this.onError(data.error);
                    } else if (data.type === "connected") {
                        this.clientId = data.client_id;
                        console.log('✅ WebSocket 连接确认，客户端 ID:', this.clientId);
                    }
                } catch (e) {
                    console.error('❌ 解析 WebSocket 消息失败:', e);
                }
            };

            this.websocket.onerror = (error) => {
                console.error('❌ WebSocket 连接错误:', error);
                this.onError('WebSocket 连接错误');
            };

            this.websocket.onclose = (event) => {
                console.log('🔌 WebSocket 连接已关闭:', event.code, event.reason);
                if (event.code !== 1000) {
                    this.onError(`WebSocket 连接异常关闭: ${event.code} - ${event.reason}`);
                }
            };

        } catch (error) {
            console.error('❌ WebAudio 录音启动失败:', error);
            this.onError('WebAudio 录音启动失败: ' + error);
        }
    }

    private async setupAudioProcessing(source: MediaStreamAudioSourceNode): Promise<void> {
        // 创建音频处理链
        const gainNode = this.audioContext!.createGain();
        gainNode.gain.value = 1.0; // 音量控制

        // 创建高通滤波器去除低频噪声
        const highpassFilter = this.audioContext!.createBiquadFilter();
        highpassFilter.type = 'highpass';
        highpassFilter.frequency.value = 80; // 80Hz 高通滤波

        // 创建低通滤波器去除高频噪声
        const lowpassFilter = this.audioContext!.createBiquadFilter();
        lowpassFilter.type = 'lowpass';
        lowpassFilter.frequency.value = 8000; // 8kHz 低通滤波

        // 连接音频处理链
        source.connect(highpassFilter);
        highpassFilter.connect(lowpassFilter);
        lowpassFilter.connect(gainNode);

        // 尝试使用 AudioWorkletNode
        try {
            await this.audioContext!.audioWorklet.addModule('/audio-processor.js');

            this.audioWorklet = new AudioWorkletNode(this.audioContext!, 'audio-processor', {
                numberOfInputs: 1,
                numberOfOutputs: 1,
                channelCount: 1
            });

            // 监听音频数据
            this.audioWorklet.port.onmessage = (event) => {
                if (event.data.type === 'audioData' && this.isRecording && this.websocket && this.websocket.readyState === WebSocket.OPEN) {
                    this.processAudioData(event.data.data);
                }
            };

            // 连接音频节点
            gainNode.connect(this.audioWorklet);
            // 不连接到 destination，避免扬声器回音
            // this.audioWorklet.connect(this.audioContext!.destination);

            console.log('✅ 使用 AudioWorkletNode');
        } catch (error) {
            console.warn('⚠️ AudioWorkletNode 不可用，回退到 ScriptProcessorNode:', error);

            // 回退到 ScriptProcessorNode
            const processor = this.audioContext!.createScriptProcessor(this.bufferSize, 1, 1);

            processor.onaudioprocess = (event) => {
                if (this.isRecording && this.websocket && this.websocket.readyState === WebSocket.OPEN) {
                    const inputBuffer = event.inputBuffer;
                    const inputData = inputBuffer.getChannelData(0);
                    this.processAudioData(inputData);
                }
            };

            // 连接音频节点
            gainNode.connect(processor);
            // 不连接到 destination，避免扬声器回音
            // processor.connect(this.audioContext!.destination);

            // 保存引用以便清理
            (this as any).processor = processor;

            console.log('✅ 使用 ScriptProcessorNode');
        }
    }

    private processAudioData(audioData: Float32Array): void {
        // 音量检测
        const volume = this.calculateVolume(audioData);
        const currentTime = Date.now();

        // 改进的静音检测逻辑
        if (volume < this.silenceThreshold) {
            // 检测到静音，直接切换到静音状态
            if (!this.isSilent) {
                this.isSilent = true;
                console.log('🔇 检测到静音，切换到静音状态');
            }
            
            // 检查是否需要发送is_speaking: false
            if (this.isSilent && !this.hasSentIsSpeakingFalse && 
                this.lastSpeechTime > 0 && currentTime - this.lastSpeechTime > this.isSpeakingFalseDelay) {
                this.sendIsSpeakingFalse();
                this.hasSentIsSpeakingFalse = true;
                console.log('🔇 静音超过2.5秒，发送is_speaking: false');
            }

            // 如果静音时间超过阈值，停止录音（只有在录音开始后才检测）
            if (this.isSilent && this.recordingStartTime > 0 && currentTime - this.lastSpeechTime > this.silenceDuration) {
                console.log('🛑 静音时间超过60秒，自动停止录音');
                this.stopRecording();
                return;
            }

            // 调试信息：显示静音时间（只在真正静音时显示）
            if (this.isSilent && this.recordingStartTime > 0 && this.lastSpeechTime > this.recordingStartTime && currentTime - this.lastSpeechTime > 1000) {
                const silenceTime = currentTime - this.lastSpeechTime;
                if (silenceTime % 10000 < 100) { // 每10秒显示一次
                    console.log(`🔇 静音时间: ${Math.round(silenceTime / 1000)}秒`);
                }
            }
        } else if (volume > this.speechThreshold) {
            // 距离检测：先进行距离检测，再决定是否更新语音状态
            // if (this.enableDistanceCheck) {
            //     const status = this.distanceDetector.getStatus();
                
            //     if (status.baseline === 0) {
            //         // 基准值为0，尝试设置基准值
            //         const baseline = this.distanceDetector.calculateBaseline(audioData);
            //         if (baseline > 0) {
            //             console.log(`🎯 基准值已设置: ${baseline.toFixed(4)}`);
            //         }
            //     } else {
            //         // 基准值不为0，检查距离
            //         const distanceResult = this.distanceDetector.checkDistance(audioData);
            //         if (!distanceResult) {
            //             console.log('⚠️ 距离检测失败，跳过后续处理');
            //             return; // 不通过就不执行后续操作
            //         }
            //     }
            // }

            // 距离检测通过后，才更新语音状态
            if (this.isSilent) {
                this.isSilent = false;
                this.lastSpeechTime = currentTime;
                console.log('🔊 检测到语音，更新lastSpeechTime');
                console.log('🔊 检测到语音，切换到语音状态');
            }
            
            // 如果已经从静音状态切换到语音状态，重置发送标志
            if (!this.isSilent && this.hasSentIsSpeakingFalse) {
                this.hasSentIsSpeakingFalse = false;
                console.log('🔊 重置is_speaking: false发送标志', this.hasSentIsSpeakingFalse);
            } else if (!this.isSilent && !this.hasSentIsSpeakingFalse) {
                console.log('🔊 语音状态，但hasSentIsSpeakingFalse已经是false，无需重置');
            }
        }

        // 噪声消除：简单的阈值过滤
        const filteredData = this.applyNoiseReduction(audioData, volume);

        // 转换为 16-bit PCM
        const pcmData = new Int16Array(filteredData.length);
        for (let i = 0; i < filteredData.length; i++) {
            pcmData[i] = Math.max(-32768, Math.min(32767, filteredData[i] * 32768));
        }

        // 只有在录音状态下才发送音频数据
        if (this.isRecording && this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.send(pcmData.buffer);
        }
    }

    private calculateVolume(audioData: Float32Array): number {
        let sum = 0;
        for (let i = 0; i < audioData.length; i++) {
            sum += audioData[i] * audioData[i];
        }
        return Math.sqrt(sum / audioData.length);
    }

    private applyNoiseReduction(audioData: Float32Array, volume: number): Float32Array {
        const filteredData = new Float32Array(audioData.length);

        // 简单的噪声消除：低于阈值的信号设为0
        const noiseThreshold = this.silenceThreshold * 2;

        for (let i = 0; i < audioData.length; i++) {
            if (Math.abs(audioData[i]) < noiseThreshold) {
                filteredData[i] = 0;
            } else {
                // 应用轻微的压缩来减少动态范围
                const sign = audioData[i] >= 0 ? 1 : -1;
                const absValue = Math.abs(audioData[i]);
                filteredData[i] = sign * Math.min(absValue, 0.8);
            }
        }

        return filteredData;
    }

    private sendIsSpeakingFalse(): void {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            const message = {
                "chunk_size": [5, 10, 5],
                "wav_name": `web_client_${this.clientId || 'unknown'}`,
                "is_speaking": false,
                "chunk_interval": 10,
                "mode": "2pass"
            };
            this.websocket.send(JSON.stringify(message));
            console.log('🔇 发送 is_speaking: false 信号');
        }
    }

    private sendStopSignal(): void {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.send(JSON.stringify({
                type: "stop",
                timestamp: Date.now()
            }));
            console.log('🛑 发送停止信号');
        }
    }

    stopRecording(): void {
        console.log('🛑 停止 WebAudio 录音...');

        this.isRecording = false;
        this.recordingStartTime = 0; // 重置录音开始时间
        this.hasSentIsSpeakingFalse = false; // 重置发送标志
        
        // 重置距离检测器
        // this.distanceDetector.reset();

        // 发送停止信号
        this.sendStopSignal();

        // 停止音频处理
        if (this.audioWorklet) {
            this.audioWorklet.disconnect();
            this.audioWorklet = null;
        }

        // 停止 ScriptProcessorNode（如果使用）
        if ((this as any).processor) {
            (this as any).processor.disconnect();
            (this as any).processor = null;
        }

        // 关闭音频上下文
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }

        // 停止媒体流
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }

        // 关闭 WebSocket
        if (this.websocket) {
            this.websocket.close(1000, "Normal closure");
            this.websocket = null;
        }

        console.log('✅ WebAudio 录音已停止');
    }

    cancelRecording(): void {
        this.stopRecording();
        if (this.onCancel) {
            this.onCancel();
        }
    }

    disconnect(): void {
        console.log('🔧 断开WebAudioRecognition连接...');
        
        // 停止录音
        this.stopRecording();
        
        // 确保WebSocket正常关闭
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.close(1000, "Component unmount");
        }
        
        // 清理所有引用
        this.websocket = null;
        this.audioContext = null;
        this.mediaStream = null;
        this.audioWorklet = null;
        this.isRecording = false;
        this.hasSentIsSpeakingFalse = false; // 重置发送标志
        
        if (this.onCancel) {
            this.onCancel();
        }
        
        console.log('✅ WebAudioRecognition已断开连接');
    }

    isRecordingState(): boolean {
        return this.isRecording;
    }
} 